import React, { useState, useRef } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const STORAGE_KEY = "ouro_beta_welcome_dismissed";

function isDismissed() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistDismissal() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // localStorage unavailable
  }
}

function clearDismissal() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
}

export default function BetaWelcomeCard({
  discordInviteUrl,
  onInviteFriend,
  schedule,
  localOpenTime,
  localCloseTime,
  isQueueOpen = false,
}) {
  const [dismissed, setDismissed] = useState(isDismissed);
  const [inviteCopied, setInviteCopied] = useState(false);
  const inviteTimerRef = useRef(null);
  const fallbackOpenTime =
    schedule?.opensAtUTC != null ? `${schedule.opensAtUTC}:00 UTC` : "";
  const fallbackCloseTime =
    schedule?.closesAtUTC != null ? `${schedule.closesAtUTC}:00 UTC` : "";
  const resolvedOpenTime = localOpenTime || fallbackOpenTime;
  const resolvedCloseTime = localCloseTime || fallbackCloseTime;
  const showScheduleWindow = Boolean(resolvedOpenTime && resolvedCloseTime);

  const handleRestore = () => {
    clearDismissal();
    setDismissed(false);
  };

  if (dismissed) {
    return (
      <Fade in timeout={300}>
        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            startIcon={<InfoOutlinedIcon fontSize="small" />}
            onClick={handleRestore}
            sx={{
              textTransform: "none",
              color: "rgba(255,255,255,0.35)",
              fontSize: "0.75rem",
              py: 0.5,
              px: 1,
              "&:hover": { color: "rgba(255,255,255,0.6)", backgroundColor: "rgba(255,255,255,0.04)" },
            }}
          >
            Show welcome info
          </Button>
        </Box>
      </Fade>
    );
  }

  const handleDismiss = () => {
    persistDismissal();
    setDismissed(true);
  };

  return (
    <Fade in timeout={400}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 1,
          position: "relative",
          borderLeftWidth: 1,
          borderLeftStyle: "solid",
          borderLeftColor: "#E8A33D",
        }}
      >
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dismiss"
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            color: "rgba(255,255,255,0.4)",
            "&:hover": { color: "rgba(255,255,255,0.7)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography
          component="h2"
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
        >
          You're in — welcome to the Ouro beta
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          We're in early beta with a growing community, so the player pool is
          still small.
        </Typography>

        {showScheduleWindow && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              py: 1,
              px: 1.5,
              borderRadius: 1,
              backgroundColor: isQueueOpen
                ? "rgba(76, 175, 80, 0.06)"
                : "rgba(232, 163, 61, 0.06)",
              border: isQueueOpen
                ? "1px solid rgba(76, 175, 80, 0.2)"
                : "1px solid rgba(232, 163, 61, 0.15)",
            }}
          >
            <AccessTimeIcon
              sx={{
                color: isQueueOpen ? "#4caf50" : "#E8A33D",
                fontSize: "1.2rem",
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              {isQueueOpen ? (
                <>
                  Queue is open now!{" "}
                  {schedule.geoQueueName
                    ? `Your region (${schedule.geoQueueName}) plays daily `
                    : "Daily schedule: "}
                  <strong style={{ color: "#4caf50" }}>
                    {resolvedOpenTime} – {resolvedCloseTime}
                  </strong>
                </>
              ) : (
                <>
                  {schedule.geoQueueName
                    ? `Your region (${schedule.geoQueueName}) plays daily from `
                    : "Queue hours are "}
                  <strong style={{ color: "#E8A33D" }}>
                    {resolvedOpenTime} – {resolvedCloseTime}
                  </strong>
                </>
              )}
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Play during peak hours, coordinate on Discord, and invite friends.
          The more players in the pool, the faster everyone queues.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            size="small"
            href={discordInviteUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: "#E8A33D",
              color: "#0B0D0F",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#F4B752",
                boxShadow: "none",
              },
            }}
          >
            Join Discord
          </Button>
          {onInviteFriend && (
            <Button
              variant="outlined"
              size="small"
              startIcon={inviteCopied ? <CheckIcon fontSize="small" /> : null}
              onClick={() => {
                onInviteFriend();
                setInviteCopied(true);
                clearTimeout(inviteTimerRef.current);
                inviteTimerRef.current = setTimeout(
                  () => setInviteCopied(false),
                  2000,
                );
              }}
              sx={{
                borderColor: inviteCopied
                  ? "rgba(76, 175, 80, 0.5)"
                  : "#E8A33D",
                color: inviteCopied
                  ? "#4caf50"
                  : "#E8A33D",
                textTransform: "none",
                "&:hover": {
                  borderColor: inviteCopied
                    ? "rgba(76, 175, 80, 0.7)"
                    : "#E8A33D",
                  backgroundColor: inviteCopied
                    ? "rgba(76, 175, 80, 0.08)"
                    : "rgba(232, 163, 61, 0.08)",
                },
              }}
            >
              {inviteCopied ? "Link copied!" : "Invite a friend"}
            </Button>
          )}
        </Box>
      </Paper>
    </Fade>
  );
}
